/**
 * Chart primitives for the wellness module. No chart library — inline SVG, zero bundle cost.
 *
 * THE COLOUR SYSTEM. Colour here means something, and it means only one thing each:
 *   dusk  — a value you recorded. Identity, never judgement.
 *   clay  — the part of a value that sits on the far side of a target you set.
 *   honey — incomplete data: a total that is still adding up, or missing a meal's numbers.
 * Nothing is coloured "good". This module records; it does not grade. Every clay or honey
 * mark is paired with a word, so nothing is carried by colour alone.
 *
 * WHY NOT ONE CHART WITH NINE LINES. It was one log-scaled panel because nine units cannot
 * share a linear axis. Small multiples solve the same problem without the log axis: each
 * metric gets its own scale, the shapes stay comparable because every panel shares one date
 * domain, and a phone can read them one at a time. Blood pressure is the exception that
 * earns a shared axis — systolic and diastolic are the same unit and only mean anything
 * together.
 */
import { useCallback, useEffect, useId, useMemo, useRef, useState } from 'react';
import type { WellnessTargetDirection, WellnessTrendSeries } from '../../shared/types';

export const DUSK = 'var(--color-dusk)';
export const CLAY = 'var(--color-clay)';
export const HONEY = 'var(--color-honey)';
const GRID = 'var(--color-border)';
const AXIS_INK = 'var(--color-ink-faint)';
const SURFACE = 'var(--color-surface)';

/** Two series on one axis need two hues. Validated for CVD separation, and direct-labelled. */
export const PAIR = [CLAY, DUSK];

// ---------------------------------------------------------------- measuring

/**
 * SVG is drawn at real pixel size rather than scaled from a fixed viewBox, because a
 * viewBox scaled from 720 down to a 343px phone shrinks every label with it — 11px type
 * arrives as 5px. Measuring costs a ResizeObserver and buys legible text at every width.
 */
export function useMeasure<T extends HTMLElement>() {
  const ref = useRef<T | null>(null);
  const [width, setWidth] = useState(0);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    setWidth(el.getBoundingClientRect().width);
    const ro = new ResizeObserver((entries) => {
      const w = entries[0]?.contentRect.width ?? 0;
      setWidth((prev) => (Math.abs(prev - w) > 0.5 ? w : prev));
    });
    ro.observe(el);
    return () => ro.disconnect();
  }, []);

  return [ref, width] as const;
}

// ---------------------------------------------------------------- numbers

/** Thousands-separated, one decimal only when the value actually has one. */
export function fmt(n: number): string {
  const rounded = Math.abs(n) >= 100 ? Math.round(n) : Math.round(n * 10) / 10;
  // `+ 0` collapses -0, which rounding produces and which prints as "-0" on an axis.
  return (rounded + 0).toLocaleString('en-US');
}

/** Short enough for an axis tick: 2.3k rather than 2,300. */
export function tick(n: number): string {
  if (Math.abs(n) >= 10000) return `${Math.round(n / 100) / 10}k`;
  return fmt(n);
}

/** Round tick values inside a domain — 0 / 50 / 100, never 37.4 / 74.8. */
function niceTicks(lo: number, hi: number, count: number): number[] {
  if (!(hi > lo)) return [lo];
  const raw = (hi - lo) / count;
  const mag = 10 ** Math.floor(Math.log10(raw));
  const norm = raw / mag;
  const step = (norm >= 5 ? 10 : norm >= 2.5 ? 5 : norm >= 1.2 ? 2 : 1) * mag;
  const out: number[] = [];
  for (let v = Math.ceil(lo / step) * step; v <= hi + 1e-9; v += step) {
    out.push(Math.round(v * 1e6) / 1e6 + 0);
  }
  return out;
}

const dayMs = 86400000;
const asTime = (isoDate: string) => new Date(`${isoDate}T00:00:00`).getTime();

/** The date extent shared by every panel, so small multiples stay comparable. */
export function dateDomain(series: WellnessTrendSeries[]): [number, number] {
  let lo = Infinity;
  let hi = -Infinity;
  for (const s of series)
    for (const p of s.points) {
      const t = asTime(p.date);
      if (t < lo) lo = t;
      if (t > hi) hi = t;
    }
  if (!Number.isFinite(lo)) return [0, 1];
  return lo === hi ? [lo - dayMs / 2, hi + dayMs / 2] : [lo, hi];
}

/**
 * The average over COMPLETE days only. Today's food total at 2pm is a third of what it
 * will be, and folding it into a mean drags the mean down by an amount that has nothing
 * to do with what was eaten. Returns null when nothing complete is left to average —
 * which the caller must render as an em dash, never as 0.
 */
export function completeAverage(s: WellnessTrendSeries): { avg: number | null; days: number } {
  const pts = s.partial_last_day ? s.points.slice(0, -1) : s.points;
  if (!pts.length) return { avg: null, days: 0 };
  const sum = pts.reduce((a, p) => a + p.value, 0);
  return { avg: sum / pts.length, days: pts.length };
}

// ---------------------------------------------------------------- the meter

export type MeterTone = 'past' | 'within' | 'none';

/** Which side of the target a value fell on. Null when there is nothing to compare. */
export function targetSide(
  value: number | null,
  target: number | null,
  direction: WellnessTargetDirection
): MeterTone {
  if (value === null || target === null || direction === 'range') return 'none';
  return (direction === 'down' ? value > target : value < target) ? 'past' : 'within';
}

/**
 * A value against the target set for it.
 *
 * THE TRACK IS THE TARGET. It runs 0 → target, so a full bar is the target reached and the
 * notch at the end is where it sits. When a value runs past its target the track extends to
 * the value instead, the notch drops back to the target's position, and the overshoot is
 * drawn in clay — the only judgement-adjacent mark on the screen, and always captioned.
 *
 * A null value gets a DASHED EMPTY TRACK, never a zero-width fill. An empty solid bar reads
 * as a measured zero, and a measured zero is the one thing that stops anyone noticing a gap.
 */
export function TargetMeter({
  value,
  target,
  direction,
  incomplete = false,
  shortfall: showShortfall = false,
}: {
  value: number | null;
  target: number | null;
  direction: WellnessTargetDirection;
  /** Some contributing entry had no number, so this total is an undercount. */
  incomplete?: boolean;
  /**
   * Mark how far a floor is from being reached. OFF by default, and deliberately off on
   * Today: at 2pm a running total is short of every floor it has, and drawing that gap is
   * a comment on the hour, not on the food. It belongs on a finished period.
   */
  shortfall?: boolean;
}) {
  const [ref, w] = useMeasure<HTMLDivElement>();
  const H = 14;
  const barY = 4;
  const barH = 6;
  const r = 3;

  const body = () => {
    if (w < 8) return null;

    if (value === null) {
      return (
        <rect
          x={0.75}
          y={barY + 0.25}
          width={Math.max(0, w - 1.5)}
          height={barH - 0.5}
          rx={r}
          fill="none"
          stroke={AXIS_INK}
          strokeWidth={1}
          strokeDasharray="3 3"
        />
      );
    }
    if (target === null || target <= 0) {
      return <rect x={0} y={barY} width={w} height={barH} rx={r} fill={DUSK} opacity={0.28} />;
    }

    const scaleMax = Math.max(value, target);
    const px = (v: number) => Math.max(0, Math.min(w, (v / scaleMax) * w));
    const notchX = Math.min(px(target), w - 1.5);
    const fillEnd = px(value);
    // Clay marks the MISS, and only the miss — which side that is depends on the direction
    // the target runs. Protein past its floor is not an overshoot to flag; sodium past its
    // ceiling is. Painting `value > target` clay regardless would scold the wrong metric.
    const side = targetSide(value, target, direction);
    const overshoot = side === 'past' && direction === 'down';
    const shortfall = showShortfall && side === 'past' && direction === 'up';
    // The 2px surface gap is what separates two fills; no stroke is drawn around either.
    const overStart = Math.min(notchX + 1, fillEnd);

    return (
      <>
        <rect x={0} y={barY} width={w} height={barH} rx={r} fill="var(--color-surface-sunk)" />
        <rect
          x={0}
          y={barY}
          width={overshoot ? notchX - 1 : fillEnd}
          height={barH}
          rx={r}
          fill={DUSK}
        />
        {overshoot && (
          <rect
            x={overStart}
            y={barY}
            width={Math.max(2, fillEnd - overStart)}
            height={barH}
            rx={r}
            fill={CLAY}
          />
        )}
        {/* The part of a floor that hasn't been reached — the same fact as an overshoot,
            drawn on the other side of the notch. */}
        {shortfall && notchX - fillEnd > 2 && (
          <rect
            x={fillEnd + 1}
            y={barY}
            width={notchX - fillEnd - 1}
            height={barH}
            rx={r}
            fill={CLAY}
            opacity={0.2}
          />
        )}
        {/* "At least this much" — the total is short by however much went unrecorded. */}
        {incomplete &&
          [0, 1, 2].map((i) => {
            const cx = Math.min(fillEnd + 5 + i * 5, w - 1.5);
            return cx > fillEnd + 2 ? (
              <circle key={i} cx={cx} cy={barY + barH / 2} r={1.4} fill={HONEY} />
            ) : null;
          })}
        <line
          x1={notchX}
          x2={notchX}
          y1={0.5}
          y2={H - 0.5}
          stroke="var(--color-ink)"
          strokeWidth={1.5}
          strokeLinecap="round"
        />
      </>
    );
  };

  return (
    <div ref={ref} className="w-full" aria-hidden>
      <svg width={Math.max(w, 1)} height={H} className="block">
        {body()}
      </svg>
    </div>
  );
}

// ---------------------------------------------------------------- line charts


/**
 * One to three series on ONE linear axis. Never two axes: a second y-scale invents a
 * correlation that isn't in the data. Series that don't share a unit get their own panel.
 */
export function SeriesChart({
  series,
  colors = [DUSK],
  domain,
  height = 168,
  compact = false,
  zeroBased = false,
  legend = false,
}: {
  series: WellnessTrendSeries[];
  colors?: string[];
  /** Shared date extent, so every panel in a grid lines up. */
  domain: [number, number];
  height?: number;
  /** Small-multiple mode: tighter chrome, no x labels (the section caption carries them). */
  compact?: boolean;
  /**
   * Anchor the axis at zero and fill the area. True for a quantity ACCUMULATED from zero —
   * a day's sodium started at nothing. False for a level that is never near zero: weight
   * plotted from 0 lb is a flat line at the top of an empty panel, and an area fill down to
   * an arbitrary floor makes the shaded height stop meaning anything.
   */
  zeroBased?: boolean;
  legend?: boolean;
}) {
  const [ref, w] = useMeasure<HTMLDivElement>();
  const gid = useId().replace(/:/g, '');
  const [hover, setHover] = useState<number | null>(null);

  const pad = compact
    ? { top: 8, right: 8, bottom: 6, left: 34 }
    : { top: 10, right: 12, bottom: 22, left: 40 };

  const dates = useMemo(() => {
    const all = new Set<string>();
    for (const s of series) for (const p of s.points) all.add(p.date);
    return [...all].sort();
  }, [series]);

  /**
   * The domain is fitted to the DATA, and stretched to include a target only when that
   * target is within reach of it. A goal weight of 210 under a 238-242 lb month would
   * otherwise squash a real four-pound trend into the top eighth of the panel and make it
   * look like a flat line. An out-of-reach target is not dropped — it becomes an
   * off-scale marker at the edge, so the chart never pretends the target isn't there.
   */
  const yDomain = useMemo(() => {
    const vals: number[] = [];
    for (const s of series) for (const p of s.points) vals.push(p.value);
    if (!vals.length) return null;
    let lo = Math.min(...vals);
    let hi = Math.max(...vals);
    if (lo === hi) {
      const step = Math.abs(lo) > 0 ? Math.abs(lo) * 0.1 : 1;
      lo -= step;
      hi += step;
    }
    const span = hi - lo;
    for (const s of series) {
      if (s.target === null) continue;
      if (s.target >= lo - span * 1.2 && s.target <= hi + span * 1.2) {
        lo = Math.min(lo, s.target);
        hi = Math.max(hi, s.target);
      }
    }
    const padY = (hi - lo) * 0.12;
    // Padding never walks a non-negative quantity below zero. A sodium axis that starts at
    // −116 mg is nonsense, and it is where the "-0" tick label came from.
    const low = lo >= 0 ? Math.max(0, lo - padY) : lo - padY;
    return { lo: zeroBased ? 0 : low, hi: hi + padY };
  }, [series, zeroBased]);

  const move = useCallback(
    (clientX: number) => {
      const el = ref.current;
      if (!el || !dates.length) return;
      const rect = el.getBoundingClientRect();
      const plotW = Math.max(1, rect.width - pad.left - pad.right);
      const t = (clientX - rect.left - pad.left) / plotW;
      const [t0, t1] = domain;
      const target = t0 + t * (t1 - t0);
      let best = 0;
      let bestD = Infinity;
      dates.forEach((d, i) => {
        const dist = Math.abs(asTime(d) - target);
        if (dist < bestD) {
          bestD = dist;
          best = i;
        }
      });
      setHover(best);
    },
    [dates, domain, pad.left, pad.right, ref]
  );

  if (!yDomain || !dates.length) return null;

  const plotW = Math.max(1, w - pad.left - pad.right);
  const plotH = Math.max(1, height - pad.top - pad.bottom);
  const [t0, t1] = domain;
  const x = (d: string) => pad.left + (t1 === t0 ? plotW / 2 : ((asTime(d) - t0) / (t1 - t0)) * plotW);
  const y = (v: number) =>
    pad.top + plotH - ((v - yDomain.lo) / (yDomain.hi - yDomain.lo)) * plotH;

  const yTicks = niceTicks(yDomain.lo, yDomain.hi, compact ? 3 : 4);
  const hoverDate = hover === null ? null : (dates[hover] ?? null);
  const inView = (v: number) => v >= yDomain.lo && v <= yDomain.hi;
  const filled = series.length === 1 && zeroBased;

  return (
    <div ref={ref} className="relative w-full">
      {w > 0 && (
        <svg
          width={w}
          height={height}
          className="block touch-pan-y"
          role="img"
          aria-label={series
            .map((s) => {
              const { avg, days } = completeAverage(s);
              return `${s.label}: ${s.points.length} days, average ${avg === null ? 'not available' : fmt(avg)} ${s.unit} over ${days} days`;
            })
            .join('. ')}
          onPointerMove={(e) => move(e.clientX)}
          onPointerLeave={() => setHover(null)}
        >
          <defs>
            {series.map((s, i) => (
              <linearGradient key={s.key} id={`${gid}-g${i}`} x1="0" y1="0" x2="0" y2="1">
                <stop offset="0%" stopColor={colors[i % colors.length]} stopOpacity={0.16} />
                <stop offset="100%" stopColor={colors[i % colors.length]} stopOpacity={0.01} />
              </linearGradient>
            ))}
          </defs>

          {yTicks.map((t) => (
            <g key={t}>
              <line
                x1={pad.left}
                x2={w - pad.right}
                y1={y(t)}
                y2={y(t)}
                stroke={GRID}
                strokeWidth={1}
              />
              <text
                x={pad.left - 6}
                y={y(t) + 3.5}
                fontSize={10}
                fill={AXIS_INK}
                textAnchor="end"
                style={{ fontVariantNumeric: 'tabular-nums' }}
              >
                {tick(t)}
              </text>
            </g>
          ))}

          {/* Targets are dashed; grid is solid. Dashing here means threshold, not decoration.
              A target outside the fitted domain gets an edge marker instead of a line, so it
              is still on the chart without flattening the data it is meant to be read against. */}
          {series.map((s, i) => {
            if (s.target === null) return null;
            // In a one-series panel the target wears axis ink: it is a line the user drew,
            // not part of the data. When several series each carry their own target, the
            // series colour is the only thing that says which target belongs to which line.
            const color = series.length > 1 ? colors[i % colors.length] : AXIS_INK;
            if (inView(s.target)) {
              return (
                <line
                  key={`t-${s.key}`}
                  x1={pad.left}
                  x2={w - pad.right}
                  y1={y(s.target)}
                  y2={y(s.target)}
                  stroke={color}
                  strokeWidth={1.25}
                  strokeDasharray="4 4"
                  opacity={0.65}
                />
              );
            }
            const below = s.target < yDomain.lo;
            // Left edge, not right: the right edge is where every series ends its line and
            // parks its dot. The surface-coloured paint-order halo keeps the label readable
            // wherever the line happens to run behind it.
            return (
              <text
                key={`t-${s.key}`}
                x={pad.left + 2}
                y={below ? pad.top + plotH - 3 : pad.top + 9}
                fontSize={10}
                fill={AXIS_INK}
                stroke={SURFACE}
                strokeWidth={3}
                paintOrder="stroke"
              >
                {below ? '▾' : '▴'} target {fmt(s.target)}
              </text>
            );
          })}

          {series.map((s, i) => {
            const color = colors[i % colors.length];
            const pts = s.points;
            if (!pts.length) return null;
            const partial = s.partial_last_day && pts.length > 1;
            const solid = partial ? pts.slice(0, -1) : pts;
            const line = solid.map((p, j) => `${j === 0 ? 'M' : 'L'} ${x(p.date)} ${y(p.value)}`).join(' ');
            // Filled only when this panel plots ONE series. Two washes over each other read
            // as a stack, and nothing here stacks — the second area is just the first one
            // tinted a different colour where they overlap.
            const area =
              filled && solid.length > 1
                ? `${line} L ${x(solid[solid.length - 1].date)} ${pad.top + plotH} L ${x(solid[0].date)} ${pad.top + plotH} Z`
                : null;
            const last = pts[pts.length - 1];
            const prev = pts[pts.length - 2];

            return (
              <g key={s.key}>
                {area && <path d={area} fill={`url(#${gid}-g${i})`} />}
                {solid.length > 1 && (
                  <path
                    d={line}
                    fill="none"
                    stroke={color}
                    strokeWidth={2}
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                )}
                {/* Today, still adding up — dashed and in the incomplete colour rather than
                    the series colour, so a third of a day's sodium reads as an unfinished
                    measurement instead of a plunge. Dropping it would hide the day you just
                    logged, which is worse; drawing it as a normal point would be a lie. */}
                {partial && prev && (
                  <path
                    d={`M ${x(prev.date)} ${y(prev.value)} L ${x(last.date)} ${y(last.value)}`}
                    fill="none"
                    stroke={HONEY}
                    strokeWidth={2}
                    strokeDasharray="3 4"
                    strokeLinecap="round"
                  />
                )}
                {/* Hollow whenever the last day is still accumulating — including the very
                    first day of use, where there is no previous point to draw a dashed leg
                    from and the dot is the only thing that can carry "not finished yet". */}
                <circle
                  cx={x(last.date)}
                  cy={y(last.value)}
                  r={compact ? 3.5 : 4}
                  fill={s.partial_last_day ? SURFACE : color}
                  stroke={s.partial_last_day ? HONEY : color}
                  strokeWidth={2}
                />
              </g>
            );
          })}

          {hoverDate &&
            (() => {
              const hx = x(hoverDate);
              return (
                <g pointerEvents="none">
                  <line
                    x1={hx}
                    x2={hx}
                    y1={pad.top}
                    y2={pad.top + plotH}
                    stroke="var(--color-ink)"
                    strokeWidth={1}
                    opacity={0.25}
                  />
                  {series.map((s, i) => {
                    const p = s.points.find((q) => q.date === hoverDate);
                    if (!p) return null;
                    return (
                      <circle
                        key={s.key}
                        cx={hx}
                        cy={y(p.value)}
                        r={4.5}
                        fill={colors[i % colors.length]}
                        stroke={SURFACE}
                        strokeWidth={2}
                      />
                    );
                  })}
                </g>
              );
            })()}

          {!compact &&
            (dates.length > 1 ? (
              <>
                <text x={pad.left} y={height - 5} fontSize={10} fill={AXIS_INK}>
                  {shortAxisDay(dates[0])}
                </text>
                <text
                  x={w - pad.right}
                  y={height - 5}
                  fontSize={10}
                  fill={AXIS_INK}
                  textAnchor="end"
                >
                  {shortAxisDay(dates[dates.length - 1])}
                </text>
              </>
            ) : (
              // Day one still gets its date. An unlabelled dot in the middle of an empty
              // panel is the first thing this module shows a new user.
              <text
                x={pad.left + plotW / 2}
                y={height - 5}
                fontSize={10}
                fill={AXIS_INK}
                textAnchor="middle"
              >
                {shortAxisDay(dates[0])}
              </text>
            ))}
        </svg>
      )}

      {hoverDate && w > 0 && (
        <div
          className="pointer-events-none absolute top-1 z-10 -translate-x-1/2 rounded-lg border border-border bg-surface px-2 py-1 text-[11px] whitespace-nowrap text-ink shadow-sm"
          style={{ left: Math.min(Math.max(x(hoverDate), 56), Math.max(w - 56, 56)) }}
        >
          <span className="text-ink-faint">{shortAxisDay(hoverDate)}</span>
          {series.map((s, i) => {
            const p = s.points.find((q) => q.date === hoverDate);
            if (!p) return null;
            return (
              <span key={s.key} className="ml-2 inline-flex items-center gap-1">
                <span
                  className="inline-block h-1.5 w-1.5 rounded-full"
                  style={{ background: colors[i % colors.length] }}
                />
                <span className="font-medium">
                  {fmt(p.value)}
                  <span className="text-ink-faint"> {s.unit}</span>
                </span>
              </span>
            );
          })}
        </div>
      )}

      {legend && (
        <ul className="mt-1 flex flex-wrap gap-x-4 gap-y-1">
          {series.map((s, i) => (
            <li key={s.key} className="flex items-center gap-1.5 text-xs text-ink-soft">
              <span
                aria-hidden
                className="inline-block h-0.5 w-4 rounded-full"
                style={{ background: colors[i % colors.length] }}
              />
              {s.label}
              {s.target !== null && (
                <span className="text-ink-faint">
                  · target {s.target_direction === 'up' ? '≥' : '≤'} {fmt(s.target)}
                </span>
              )}
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

const shortAxisDay = (d: string) =>
  new Date(`${d}T00:00:00`).toLocaleDateString('en-US', { month: 'short', day: 'numeric' });

// ---------------------------------------------------------------- the numbers

/**
 * Every chart's table twin. A tooltip is never the only way to reach a value — this is
 * the path for a screen reader, for a printout, and for anyone who wants the actual number.
 */
export function NumbersTable({ series }: { series: WellnessTrendSeries[] }) {
  const dates = useMemo(() => {
    const all = new Set<string>();
    for (const s of series) for (const p of s.points) all.add(p.date);
    return [...all].sort().reverse();
  }, [series]);

  return (
    <details className="mt-3 border-t border-border pt-2">
      {/* `list-none` alone leaves the disclosure triangle in iOS Safari, which is the browser
          this app is actually used in. The webkit pseudo-element has to go too. */}
      <summary className="inline-flex min-h-11 cursor-pointer list-none items-center text-xs font-medium text-ink-soft focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-sage [&::-webkit-details-marker]:hidden">
        Show the numbers
      </summary>
      <div className="mt-1 overflow-x-auto">
        <table className="w-full min-w-max text-left text-xs">
          <thead>
            <tr className="text-ink-faint">
              <th scope="col" className="py-1 pr-3 font-medium">
                Day
              </th>
              {series.map((s) => (
                <th key={s.key} scope="col" className="py-1 pr-3 text-right font-medium">
                  {s.label} <span className="font-normal">({s.unit})</span>
                </th>
              ))}
            </tr>
          </thead>
          <tbody className="text-ink-soft">
            {dates.map((d) => (
              <tr key={d} className="border-t border-border">
                <th scope="row" className="py-1 pr-3 font-normal whitespace-nowrap text-ink">
                  {shortAxisDay(d)}
                </th>
                {series.map((s) => {
                  const p = s.points.find((q) => q.date === d);
                  const partial = s.partial_last_day && d === s.points[s.points.length - 1]?.date;
                  return (
                    <td
                      key={s.key}
                      className="py-1 pr-3 text-right"
                      style={{ fontVariantNumeric: 'tabular-nums' }}
                    >
                      {p ? fmt(p.value) : '—'}
                      {p && partial && <span className="text-honey"> so far</span>}
                    </td>
                  );
                })}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </details>
  );
}
